(() => {
  const roots = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initRoot(root) {
    if (roots.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const cleanups = [];

    root.querySelectorAll('[data-home-hero]').forEach((hero) => {
      const slides = [...hero.querySelectorAll('[data-home-hero-slide]')];
      const controls = [...hero.querySelectorAll('[data-home-hero-go]')];
      if (!slides.length) return;

      let index = 0;
      let requestId = 0;
      let timer;
      const mobile = window.matchMedia('(max-width: 767px)');
      const stopTimer = () => {
        window.clearInterval(timer);
        timer = undefined;
      };
      const poster = hero.querySelector('[data-home-hero-poster]');
      let posterReady = !poster;
      const syncVideo = () => slides.forEach((slide, slideIndex) => {
        slide.querySelectorAll('video').forEach((video) => {
          video.muted = true;
          video.playsInline = true;
          if (slideIndex === index && !document.hidden && posterReady) video.play()?.catch(() => {});
          else video.pause();
        });
      });
      const revealVideo = () => {
        const video = hero.querySelector('[data-home-hero-video]');
        if (!video || !poster) return;
        if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(() => { if (!signal.aborted) (poster.closest('[data-home-hero-poster-wrap]') || poster).classList.add('is-video-ready'); });
        else if (video.readyState >= 2) (poster.closest('[data-home-hero-poster-wrap]') || poster).classList.add('is-video-ready');
      };
      const releasePoster = async () => {
        if (posterReady || signal.aborted) return;
        if (poster.complete && poster.naturalWidth) await poster.decode().catch(() => {});
        if (signal.aborted) return;
        posterReady = true;
        syncVideo();
      };
      if (poster) {
        poster.addEventListener('load', releasePoster, { signal });
        poster.addEventListener('error', releasePoster, { signal });
        hero.querySelector('[data-home-hero-video]')?.addEventListener('playing', revealVideo, { signal });
        if (poster.complete) releasePoster();
      }
      const prepareImage = async (image) => {
        if (!image.complete) {
          const loaded = await new Promise((resolve) => {
            const finish = (ready) => {
              image.removeEventListener('load', onLoad);
              image.removeEventListener('error', onError);
              signal.removeEventListener('abort', onError);
              resolve(ready);
            };
            const onLoad = () => finish(true);
            const onError = () => finish(false);
            image.addEventListener('load', onLoad, { once: true });
            image.addEventListener('error', onError, { once: true });
            signal.addEventListener('abort', onError, { once: true });
            image.fetchPriority = 'high';
            image.loading = 'eager';
          });
          if (!loaded) return false;
        }
        if (!image.naturalWidth || signal.aborted) return false;
        try { await image.decode(); } catch { return false; }
        return !signal.aborted;
      };
      const show = async (next) => {
        const requestedIndex = (next + slides.length) % slides.length;
        const currentRequest = ++requestId;
        hero.setAttribute('aria-busy', 'true');
        const images = [...slides[requestedIndex].querySelectorAll('img')];
        const ready = await Promise.all(images.map(prepareImage));
        if (signal.aborted || currentRequest !== requestId) return;
        hero.setAttribute('aria-busy', 'false');
        if (ready.some((value) => !value)) {
          hero.dataset.mediaLoadError = '1';
          return;
        }
        delete hero.dataset.mediaLoadError;
        index = requestedIndex;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        controls.forEach((button, buttonIndex) => {
          const active = buttonIndex === index;
          button.classList.toggle('is-active', active);
          if (active) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        syncVideo();
      };
      const startTimer = () => {
        stopTimer();
        if (slides.length > 1 && !mobile.matches && !reducedMotion.matches && !document.hidden) {
          timer = window.setInterval(() => show(index + 1), 9000);
        }
      };
      controls.forEach((button) => button.addEventListener('click', () => {
        stopTimer();
        show(Number(button.dataset.homeHeroGo || 0));
      }, { signal }));
      const mediaChange = () => {
        if (mobile.matches) {
          stopTimer();
          show(0);
        } else startTimer();
      };
      mobile.addEventListener?.('change', mediaChange, { signal });
      reducedMotion.addEventListener?.('change', startTimer, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          stopTimer();
          slides.forEach((slide) => slide.querySelectorAll('video').forEach((video) => video.pause()));
        } else {
          syncVideo();
          startTimer();
        }
      }, { signal });
      show(0);
      startTimer();
      cleanups.push(stopTimer);
    });

    root.querySelectorAll('.ms-home-categories').forEach((section) => {
      const track = section.querySelector('.mts-helmet-types__track');
      const cards = [...section.querySelectorAll('.mts-helmet-type')];
      const previous = section.querySelector('.mts-helmet-types__previous');
      const next = section.querySelector('.mts-helmet-types__next');
      const dots = [...section.querySelectorAll('.mts-helmet-types__dots i')];
      if (!track || !cards.length) return;

      const cardStep = () => {
        const card = cards[0];
        return card.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).gap) || 12);
      };
      const activeIndex = () => Math.min(cards.length - 1, Math.max(0, Math.round(track.scrollLeft / cardStep())));
      const sync = () => {
        const index = activeIndex();
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
        if (previous) previous.disabled = track.scrollLeft < 2;
        if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      };
      const move = (direction) => track.scrollBy({
        left: direction * cardStep(),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
      previous?.addEventListener('click', () => move(-1), { signal });
      next?.addEventListener('click', () => move(1), { signal });
      track.addEventListener('scroll', sync, { passive: true, signal });
      window.addEventListener('resize', sync, { passive: true, signal });
      sync();
    });

    root.querySelectorAll('[data-oem-slides]').forEach((track) => {
      const section = track.closest('[data-oem-reference]');
      const previous = section?.querySelector('[data-oem-prev]');
      const next = section?.querySelector('[data-oem-next]');
      const status = section?.querySelector('[data-process-status]');
      const items = [...track.children];
      if (!section || !items.length) return;

      const step = () => {
        const first = items[0];
        return first.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).gap) || 0);
      };
      const sync = () => {
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        if (previous) previous.disabled = track.scrollLeft < 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        if (status) {
          const pageSize = step();
          const pages = Math.max(1, Math.ceil(max / pageSize) + 1);
          const current = Math.min(pages, Math.max(1, Math.round(track.scrollLeft / pageSize) + 1));
          status.textContent = `${current} / ${pages}`;
        }
      };
      const move = (direction) => track.scrollBy({
        left: direction * step(),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
      previous?.addEventListener('click', () => move(-1), { signal });
      next?.addEventListener('click', () => move(1), { signal });
      track.addEventListener('scroll', sync, { passive: true, signal });
      track.addEventListener('keydown', (event) => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });
      window.addEventListener('resize', sync, { passive: true, signal });
      sync();
    });

    roots.set(root, () => {
      controller.abort();
      cleanups.forEach((cleanup) => cleanup());
      roots.delete(root);
    });
  }

  function setup(scope = document) {
    const section = scope.matches?.('.shopify-section') ? scope : scope.closest?.('.shopify-section');
    if (section?.closest('.ms-home-main, .ms-product-main')) initRoot(section);
    if (scope.matches?.('.ms-home-main, .ms-product-main')) {
      scope.querySelectorAll(':scope > .shopify-section').forEach(initRoot);
    } else {
      scope.querySelectorAll('.ms-home-main > .shopify-section, .ms-product-main > .shopify-section').forEach(initRoot);
    }
  }

  function teardown(scope) {
    if (!scope) return;
    const section = scope.matches?.('.shopify-section') ? scope : scope.closest?.('.shopify-section');
    [...roots.keys()].forEach((root) => {
      if (root === section || root === scope || scope.contains?.(root)) roots.get(root)();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setup(), { once: true });
  } else setup();
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
})();
